// useUnLikeProperty
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "../../../api/axios";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import useProperty from "../../context/useProperty";

export const useUnLikeProperty = (
  loginType: string,
  onSuccess?: any,
  onError?: any
) => {
  const axios = useAxios(loginType);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const {
    coords
  }: // setCoords,
  // hoveredProperty,
  // setHoveredProperty,
  // collapsed
  any = useProperty();
  const [searchParams] = useSearchParams();
  const queryParams = new URLSearchParams(location.search);
  // const panel = Number(queryParams.get("panel"));
  const page = Number(queryParams.get("page")) || 1;
  const limit = Number(queryParams.get("limit")) || 10;
  const searchTextValue = searchParams.get("searchText");
  const stateValue = searchParams.get("state");
  const offMarketValue = searchParams.get("offMarket");
  const occupancyValue = searchParams.get("occupancy");
  const salesInstructionsValue = searchParams.get("salesInstructions");
  const statusValue = searchParams.get("status");
  const possessionDeliveryValue = searchParams.get("possessionDelivery");
  const sortValue = searchParams.get("sort");
  const orderValue = searchParams.get("order");

  const unLikePropertyMutation: any = useMutation({
    mutationFn: async (propertyId: string) => {
      const urlKey = `${t("remove-like-property-api")}/${propertyId}`;
      const res = await axios.delete(urlKey);
      return res;
    },
    onSuccess: async (data, propertyId) => {
      let payload: any = {
        listType: "map",
        mapBounds: coords,
        state: stateValue || "CA",
        ...(searchTextValue && { searchText: searchTextValue })
      };
      if (searchTextValue) {
        payload = { ...payload, searchTxt: searchTextValue };
      }
      if (stateValue) {
        payload = { ...payload, state: stateValue };
      }
      if (offMarketValue) {
        payload = { ...payload, offMarket: offMarketValue === "Yes" };
      }
      if (occupancyValue) {
        payload = { ...payload, occupancy: occupancyValue };
      }
      if (salesInstructionsValue) {
        payload = { ...payload, salesInstruction: salesInstructionsValue };
      }
      if (statusValue) {
        payload = { ...payload, status: statusValue };
      }
      if (possessionDeliveryValue) {
        payload = { ...payload, possession: possessionDeliveryValue };
      }
      if (sortValue) {
        payload = { ...payload, orderBy: sortValue };
      }
      if (orderValue) {
        payload = { ...payload, order: orderValue };
      }
      await queryClient.setQueryData(
        ["MapPublishProperties", payload],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 0 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );

      await queryClient.setQueryData(
        ["MapInvestorProperties", payload],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 0 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );

      await queryClient.setQueryData(
        ["Recent-Properties", page, limit],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 0 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );
      await queryClient.setQueryData(
        [`Recent-Properties-infinite-list`],
        (oldData: any) => {
          if (!oldData) return oldData;

          const updatedPages = oldData.pages.map((page: any) =>
            page.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 0 }
                : property
            )
          );
          return { ...oldData, pages: updatedPages };
        }
      );

      await queryClient.setQueryData(
        [`Your-Bid-infinite-list`],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedPages = oldData.pages.map((page: any) =>
            page.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 0 }
                : property
            )
          );
          return { ...oldData, pages: updatedPages };
        }
      );

      await queryClient.setQueryData(
        ["Your-Bids", page, limit],
        (oldData: any) => {
          if (!oldData) return oldData;

          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 0 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );

      await queryClient.setQueryData(
        ["Your-Bids", page, limit],
        (oldData: any) => {
          if (!oldData) return oldData;

          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 0 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );

      await queryClient.setQueryData(
        [`investor-property-list`, coords, searchTextValue, sortValue],
        (oldData: any) => {
          if (!oldData) return oldData;

          // Since infinite query returns an array of pages, we need to update the correct page
          const updatedPages = oldData.pages.map((page: any) => {
            const updatedProperties = page.data.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 0 }
                : property
            );
            return { ...page, data: updatedProperties };
          });

          return { ...oldData, pages: updatedPages };
        }
      );

      await queryClient.setQueryData(
        [
          `Property-List`,
          coords,
          searchTextValue,
          stateValue,
          offMarketValue,
          occupancyValue,
          salesInstructionsValue,
          statusValue,
          possessionDeliveryValue,
          sortValue
        ],
        (oldData: any) => {
          if (!oldData) return oldData;

          // Since infinite query returns an array of pages, we need to update the correct page
          const updatedPages = oldData.pages.map((page: any) => {
            const updatedProperties = page.data.map((property: any) =>
              property.id === propertyId
                ? { ...property, favCount: 0 }
                : property
            );
            return { ...page, data: updatedProperties };
          });

          return { ...oldData, pages: updatedPages };
        }
      );

      await queryClient.refetchQueries({
        queryKey: ["Saved-Properties", page, limit]
      });
      await queryClient.refetchQueries({
        queryKey: ["Saved-Properties-infinite-list"]
      });

      await queryClient.setQueryData(
        [`Property-Detail`, propertyId],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = { ...oldData.data, favCount: 0 };
          return { ...oldData, data: updatedData };
        }
      );
      await queryClient.setQueryData(
        [`Related-Property-List`, propertyId],
        (oldData: any) => {
          if (!oldData) return oldData;
          const updatedData = oldData.data.map((property: any) =>
            property.id === propertyId ? { ...property, favCount: 0 } : property
          );

          return { ...oldData, data: updatedData };
        }
      );
      // await setHoveredProperty({ ...hoveredProperty, favCount: 0 });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (err) => {
      if (onError) {
        onError(err);
      }
    }
  });
  async function unLikeProperty(e: any, propertyId: string) {
    e.stopPropagation();
    unLikePropertyMutation.mutate(propertyId);
  }

  return { unLikeProperty };
};
